import { none, Option, some } from '@sapphire/result';

/** Which qualifications a set of quotas targets — shared by feasibility and standard-survey payload mappers. */
export class NonQuotaTargeting {
  private constructor(
    private readonly _targetedCodes: ReadonlySet<number>,
    private readonly _overriddenByEveryQuota: ReadonlySet<number>,
  ) {}

  /** Admitted quotas only — a blank drawer row's qualification is targeted by nobody. */
  public static from(
    codesPerQuota: readonly ReadonlySet<number>[],
  ): NonQuotaTargeting {
    return new NonQuotaTargeting(
      new Set(codesPerQuota.flatMap((codes) => [...codes])),
      codesTargetedByEveryQuota(codesPerQuota),
    );
  }

  /** Every criterion on a quota row states a flag, the quota's own included. */
  public flagForQuotaRow(qualificationCode: number): boolean {
    return !this._targetedCodes.has(qualificationCode);
  }

  /** None means no verdict — every quota overrides this default, so the key ships omitted. */
  public flagForDefaultCriterion(qualificationCode: number): Option<boolean> {
    return this._overriddenByEveryQuota.has(qualificationCode)
      ? none
      : some(this.flagForQuotaRow(qualificationCode));
  }
}

/** Empty for an empty quota list: with no quotas, nothing is overridden. */
function codesTargetedByEveryQuota(
  codesPerQuota: readonly ReadonlySet<number>[],
): ReadonlySet<number> {
  return Option.from(codesPerQuota.at(0)).mapOr(
    new Set<number>(),
    (first) =>
      new Set(
        [...first].filter((code) =>
          codesPerQuota.every((codes) => codes.has(code)),
        ),
      ),
  );
}
